import React from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Link from '@tiptap/extension-link';
import { useFormat } from '../../../components/FormatContext';
import { senderLabel, visibilityNote } from '../utils/requestHelpers';

const URL_REGEX = /(https?:\/\/[^\s]+)/g;
const isUrl = (value) => /^https?:\/\/[^\s]+$/.test(value);

function renderTextWithLinks(text) {
    const lines = String(text || '').split('\n');

    return lines.map((line, lineIndex) => {
        const parts = line.split(URL_REGEX);

        return (
            <React.Fragment key={lineIndex}>
                {parts.map((part, partIndex) => {
                    if (isUrl(part)) {
                        return (
                            <a
                                key={`${lineIndex}-${partIndex}`}
                                href={part}
                                target="_blank"
                                rel="noreferrer"
                                style={{ wordBreak: 'break-all' }}
                            >
                                {part}
                            </a>
                        );
                    }

                    return (
                        <React.Fragment key={`${lineIndex}-${partIndex}`}>
                            {part}
                        </React.Fragment>
                    );
                })}
                {lineIndex < lines.length - 1 ? <br /> : null}
            </React.Fragment>
        );
    });
}

function parseTipTapJsonString(value) {
    if (typeof value !== 'string' || !value.trim()) return null;

    try {
        const parsed = JSON.parse(value);

        if (
            parsed &&
            typeof parsed === 'object' &&
            parsed.type === 'doc' &&
            Array.isArray(parsed.content)
        ) {
            return parsed;
        }

        return null;
    } catch {
        return null;
    }
}

const ReadonlyRichContent = ({ json }) => {
    const editor = useEditor({
        extensions: [
            StarterKit,
            Link.configure({
                openOnClick: true,
                autolink: true,
                linkOnPaste: true,
            }),
        ],
        content: json,
        editable: false,
        immediatelyRender: false,
    });

    if (!editor) return null;

    return <EditorContent editor={editor} className="tiptap message-rich-content" />;
};

const CollapsibleContent = ({ children, collapsedHeight = 220 }) => {
    const contentRef = React.useRef(null);
    const [expanded, setExpanded] = React.useState(false);
    const [canExpand, setCanExpand] = React.useState(false);

    React.useEffect(() => {
        const el = contentRef.current;
        if (!el) return;

        const checkOverflow = () => {
            setCanExpand(el.scrollHeight > collapsedHeight + 8);
        };

        checkOverflow();

        const resizeObserver = new ResizeObserver(() => {
            checkOverflow();
        });

        resizeObserver.observe(el);

        return () => {
            resizeObserver.disconnect();
        };
    }, [children, collapsedHeight]);

    return (
        <div>
            <div
                style={{
                    position: 'relative',
                }}
            >
                <div
                    ref={contentRef}
                    style={{
                        maxHeight: !expanded && canExpand ? collapsedHeight : 'none',
                        overflow: 'hidden',
                        transition: 'max-height 0.2s ease',
                    }}
                >
                    {children}
                </div>

                {!expanded && canExpand && (
                    <div
                        style={{
                            position: 'absolute',
                            left: 0,
                            right: 0,
                            bottom: 0,
                            height: 56,
                            background:
                                'linear-gradient(to bottom, rgba(255,255,255,0), rgba(255,255,255,0.95))',
                            pointerEvents: 'none',
                        }}
                    />
                )}
            </div>

            {canExpand && (
                <div className="mt-2">
                    <button
                        type="button"
                        className="btn btn-link btn-sm p-0 text-decoration-none"
                        onClick={() => setExpanded((v) => !v)}
                    >
                        {expanded ? 'Show less' : 'Show more'}
                    </button>
                </div>
            )}
        </div>
    );
};

const MessageBubble = ({ msg, isAdmin = false }) => {
    const { baseFormatters } = useFormat();

    const isInbound = msg.direction === 'IN';
    const isEvent = !!msg.isEvent;
    const isInternal = !!msg.isInternal;

    if (isEvent) {
        return (
            <div className="d-flex justify-content-center my-2">
                <div
                    className="px-3 py-1 rounded-pill small text-muted border bg-light"
                    style={{ maxWidth: '90%' }}
                >
                    {msg.content || '—'}
                    {msg.createdAt && (
                        <>
                            {' · '}
                            {baseFormatters.datetime.format(new Date(msg.createdAt))}
                        </>
                    )}
                </div>
            </div>
        );
    }

    const wrapperClass = isInbound ? 'justify-content-start' : 'justify-content-end';

    const bubbleClass = isInternal
        ? 'bg-warning-subtle border'
        : isInbound
            ? 'bg-white border'
            : 'bg-light border';

    const note = isAdmin ? visibilityNote(msg.visibility) : '';
    const parsedJson = parseTipTapJsonString(msg.content);

    return (
        <div className={`d-flex ${wrapperClass} mb-3`}>
            <div
                className={`rounded-3 p-3 ${bubbleClass}`}
                style={{ maxWidth: '76%' }}
            >
                <div className="d-flex justify-content-between align-items-start gap-3 mb-2">
                    <div className="fw-semibold">{senderLabel(msg)}</div>

                    <div className="text-muted small text-nowrap">
                        {msg.createdAt
                            ? baseFormatters.datetime.format(new Date(msg.createdAt))
                            : ''}
                    </div>
                </div>

                <CollapsibleContent collapsedHeight={220}>
                    <div
                        style={{
                            lineHeight: 1.7,
                            wordBreak: 'break-word',
                            overflowWrap: 'anywhere',
                            fontSize: 14,
                        }}
                    >
                        {parsedJson ? (
                            <ReadonlyRichContent json={parsedJson} />
                        ) : (
                            <div style={{ whiteSpace: 'pre-wrap' }}>
                                {renderTextWithLinks(msg.content || '—')}
                            </div>
                        )}
                    </div>
                </CollapsibleContent>

                {note && (
                    <div className="text-muted small mt-2 pt-2 border-top">
                        {note}
                    </div>
                )}
            </div>
        </div>
    );
};

export default MessageBubble;